import { ArrowPathIcon, QrCodeIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { useState } from 'react';

import { Button } from '@/components/core/Button';
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogHeaderDefault,
} from '@/components/core/Dialog';
import { IconButton } from '@/components/core/IconButton';
import { Loader } from '@/components/core/Loader';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';

import { contactErrorMessage, formatContactDate } from './businessPartner';
import { ContactPublication, useContactPublications, useRepublishPublication } from './businessPartner.queries';

const ContactPublications = ({ organizationId }: { organizationId: number }) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const publications = useContactPublications(organizationId, true);
    const republish = useRepublishPublication();
    const [confirmId, setConfirmId] = useState<number | undefined>();

    const confirmRepublish = () => {
        if (confirmId == null) {
            return;
        }
        const publicationId = confirmId;
        republish.mutate(publicationId, {
            onSuccess: () => {
                toast.success('COMMUNICATION_CHANNELS.REPUBLICATION_STARTED_SUCCESSFULLY');
                setConfirmId(undefined);
                window.setTimeout(() => {
                    void publications.refetch();
                }, 1000);
            },
            onError: (error) => {
                const data = axios.isAxiosError(error) ? error.response?.data : undefined;
                toast.error(contactErrorMessage(data, t, 'COMMUNICATION_CHANNELS.ERROR_STARTING_REPUBLICATION')
                    ?? t('COMMUNICATION_CHANNELS.ERROR_STARTING_REPUBLICATION'));
            },
        });
    };

    return (
        <div className="tw-w-full tw-border tw-border-solid tw-border-primary tw-p-3">
            <h3 className="tw-mb-2 tw-text-t-sm tw-font-bold tw-text-h-primary lg:tw-text-t-base">
                {t('PUBLICATIONS')}
            </h3>
            {publications.isLoading ? <Loader /> : null}
            <table className="tw-w-full tw-border-collapse tw-border-0">
                <tbody>
                    {(publications.data ?? []).map((publication: ContactPublication) => (
                        <tr className="publication-details" key={publication.id}>
                            <td className="tw-py-1 tw-pr-3 tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base">
                                {formatContactDate(publication.creationDate, true)}
                            </td>
                            <td className="tw-py-1 tw-pr-3 tw-text-t-sm tw-text-t-primary lg:tw-text-t-base">
                                {t(publication.taskStatus ?? '')}
                                <span className="tw-text-t-secondary">
                                    {` ( ${publication.publishedItemsCount ?? 0} ${t('OF')} ${publication.totalItemsCount ?? 0} )`}
                                </span>
                            </td>
                            <td className="tw-py-1 tw-text-right">
                                <div className="tw-inline-flex tw-items-center tw-justify-end">
                                    <IconButton
                                        id={`restart-publication${publication.id}`}
                                        icon={ArrowPathIcon}
                                        size="md"
                                        className="tw-icon-primary"
                                        title={t('COMMUNICATION_CHANNELS.REPUBLISH_ITEMS')}
                                        aria-label={t('COMMUNICATION_CHANNELS.REPUBLISH_ITEMS')}
                                        onClick={() => setConfirmId(publication.id)}
                                    />
                                    <a
                                        id={`browse-publication${publication.id}`}
                                        className="icon-button tw-icon-primary tw-inline-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-bg-transparent"
                                        href={`browse?publicationTaskId=${publication.id}`}
                                        title={t('COMMUNICATION_CHANNELS.SHOW_ITEMS')}
                                        aria-label={t('COMMUNICATION_CHANNELS.SHOW_ITEMS')}
                                    >
                                        <QrCodeIcon className="tw-h-5 tw-w-5 lg:tw-h-6 lg:tw-w-6" />
                                    </a>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <Dialog open={confirmId != null} onOpenChange={(open) => { if (!open) setConfirmId(undefined); }}>
                <DialogContent variant="small">
                    <DialogHeader>
                        <DialogHeaderDefault>{t('MODAL.CONFIRM_REQUIRED')}</DialogHeaderDefault>
                    </DialogHeader>
                    <DialogBody>
                        {t('COMMUNICATION_CHANNELS.REPUBLISH_ITEMS')}
                    </DialogBody>
                    <DialogFooter>
                        <Button type="button" onClick={confirmRepublish} loading={republish.isPending}>
                            {t('YES')}
                        </Button>
                        <Button type="button" variant="secondary" onClick={() => setConfirmId(undefined)}>
                            {t('NO')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export { ContactPublications };
